import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  memo,
} from "react";
import axios from "axios";
import {
  Trash2,
  Edit,
  Plus,
  Printer,
  X,
  Check,
  Loader2,
  Lightbulb,
} from "lucide-react";
import { fetchItems } from "../../../store/itemSlice";
import { IndentPDF } from "../../../components/Header/IndentPDF";
import PrinterButton from "../IndentComponents/PrinterButton";
import { useDispatch, useSelector } from "react-redux";
import SearchRow from "../IndentComponents/SearchRow";
import Modal from "../IndentComponents/Modal";
import DeleteConfirmationModal from "../IndentComponents/DeleteConfirmationModal";
import MobileOptimizedModal from "../IndentComponents/MobileOptimizedModal";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const PHItemsTable = ({
  indentID,
  indentType,
  onAddItem,
  onDeleteItem,
  calculateTotalAmount,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1a: 0, l1: 0, l2: 0, po: 0, ph: 0 },
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state
  const [accountingUnits, setAccountingUnits] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [newItem, setNewItem] = useState({
    categoryID: "",
    itemID: "",
    quantity: 1,
    stock: 0,
    total: 0,
    remarks: "",
    normalDays: 0,
    emergencyDays: 0,
    promisedDate: "",
    commitedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
  });
  const [pdfError, setPdfError] = useState("");
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const isAdmin = role?.toLowerCase() === 'admin';
  const [editingDate, setEditingDate] = useState(null);

  // Function to fetch indent data for PDF
  const fetchIndentData = async () => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/phpage/indents/${indentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    // Fetch items with category information
    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/phpage/indentitems/${indentID}`
    );
    const itemsData = await itemsResponse.json();

    return {
      indentId: indentID,
      creationDate: indentDetails?.CreationDate,
      PEApprovalDate: indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate || null,
      L1ApprovedDate: indentDetails?.L1ApprovedDate || null,
      L2ApprovalDate: indentDetails?.L2ApprovalDate || null,
      projectName: indentDetails?.ProjectName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      status: indentDetails?.Status || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      invoicingType: indentDetails?.TypeName || "N/A",
      // Add approval flags
      PO: indentDetails?.PO || 0,
      L1: indentDetails?.L1 || 0,
      L2: indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
      })),
    };
  };

  // Function to get PDF component
  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  useEffect(() => {
    if (indentID) {
      dispatch(fetchItems(indentID))
        .unwrap()
        .then((items) => {
          items.forEach((item) => {
            if (!item.description) {
              fetchItemDescription(item.itemID);
            }
          });
        })
        .catch((error) => {
          console.error("Error fetching items:", error);
        });
    }
  }, [dispatch, indentID]);

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    itemIndex: null,
    itemName: "",
  });

  // Modified handleDeleteItem to handle the confirmation flow
  const handleDeleteClick = (index, itemName) => {
    setDeleteConfirmation({
      isOpen: true,
      itemIndex: index,
      itemName: itemName,
    });
  };

  const handleDeleteConfirm = async () => {
    const index = deleteConfirmation.itemIndex;
    const itemToDelete = items[index];

    try {
      await axios.delete(
        `${API_BASE_URL}/viewTable/indentitems/${itemToDelete.indentItemID}`,
        {
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      if (onDeleteItem) {
        onDeleteItem(index);
      }

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    } finally {
      setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
  };

  // ... existing state
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.itemID && !itemDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID);
        }
      });
    }
  }, [items.length]); // Only depend on items.length, not the entire items array

  const refreshItemsData = async () => {
    if (!indentID) return;

    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          // Format the CommitedDate to dd-mm-yy
          const formattedDate = item.CommitedDate
            ? new Date(item.CommitedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            normalDays: item.NormalDays,
            emergencyDays: item.EmergencyDays,
            commitedDate: formattedDate, // Use the formatted date
            accountingUnitID: item.AccountingUnitID,
            unitname: item.AU, // Make sure this is included
          };
        });

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error refreshing items:", error);
      setError("Failed to refresh items. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Memoized function to fetch items
  const fetchItems2 = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          // Format the PromisedDate to dd-mm-yy
          const formattedDate = item.PromisedDate
            ? new Date(item.PromisedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;
          const formattedDate2 = item.CommitedDate
            ? new Date(item.CommitedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: formattedDate, // Use the formatted date
            commitedDate: formattedDate2, // Use the formatted date
            accountingUnitID: item.AccountingUnitID,
            unitname: item.AU,
          };
        });

      setItems(fetchedItems);
      return fetchedItems;
    } catch (error) {
      console.error("Error fetching items:", error);
      return [];
    }
  };

  // Fetch categories and items when the component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [categoryResponse, itemResponse, accountingUnitsResponse] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
            axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
            axios.get(`${API_BASE_URL}/dropdown/maudrop`),
          ]);

        setCategories(categoryResponse.data || []);
        setAllItems(itemResponse.data || []);
        setAccountingUnits(accountingUnitsResponse.data || []);

        // Fetch items separately to avoid race conditions
        await fetchItems2();
      } catch (error) {
        console.error("Error fetching data:", error);
        setCategories([]);
        setAllItems([]);
        setItems([]);
      }
    };

    if (indentID) {
      fetchAllData();
    }
  }, [indentID]);

  const handleItemChange = (itemId) => {
    const parsedItemId = parseInt(itemId, 10);
    const selectedItem = filteredItems.find(
      (item) => item.ItemID === parsedItemId
    );

    setNewItem((prev) => ({
      ...prev,
      itemID: parsedItemId,
      accountingUnitID: selectedItem?.AccountingUnitID || "",
              accountingUnitName: selectedItem?.AU || "",
      stock: selectedItem?.Stock || 0,
    }));
  };


  const handleCategoryChange = (categoryId) => {
  if (!categoryId) {
    setNewItem((prev) => ({
      ...prev,
      categoryID: "",
      itemID: "", 
      normalDays: 0,
      emergencyDays: 0,
      commitedDate: "",
      accountingUnitID: "",
      accountingUnitName: "",
    }));
    setFilteredItems([]);
    return;
  }
  const parsedCategoryId = parseInt(categoryId, 10);
  const selectedCategory = categories.find(
    (cat) => cat.CategoryID === parsedCategoryId
  );

  if (selectedCategory) {
    const currentDate = new Date();
    const promisedDate = new Date(currentDate);
    const daysToAdd = indentType === 'normal' ? 
      (selectedCategory?.NormalDays || 0) : 
      (selectedCategory?.EmergencyDays || 0);
      
    promisedDate.setDate(currentDate.getDate() + daysToAdd);

    setNewItem((prev) => ({
      ...prev,
      categoryID: parsedCategoryId,
      itemID: "", // Reset itemID
      normalDays: selectedCategory?.NormalDays || 0,
      emergencyDays: selectedCategory?.EmergencyDays || 0,
      promisedDate: promisedDate.toISOString().split("T")[0],
      commitedDate: promisedDate.toISOString().split("T")[0],
      accountingUnitID: "", // Reset accountingUnitID
      accountingUnitName: "", // Reset accountingUnitName
    }));

    // Filter items based on selected category
    const filteredItems = allItems.filter(
      (item) => item.CategoryID === parsedCategoryId
    );
    setFilteredItems(filteredItems);
  }
};

  const handleQuantityChange = (value) => {
    // Allow decimal values up to 3 places
    const regex = /^\d*\.?\d{0,3}$/;
    if (regex.test(value)) {
      setNewItem((prev) => ({
        ...prev,
        quantity: value,
      }));
    }
  };

  const calculatePromisedDate = (days) => {
    if (!days || isNaN(days)) return "";

    const today = new Date();
    const promiseDate = new Date(today);
    promiseDate.setDate(today.getDate() + parseInt(days));
    return promiseDate.toISOString().split("T")[0];
  };

  const handleNewItemFieldChange = useCallback((field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = Math.round(parseFloat(value));
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else if (field === "normalDays") {
      const promisedDate = calculatePromisedDate(value);
      setNewItem((prev) => ({
        ...prev,
        [field]: value,
        promisedDate: promisedDate,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleAddNewItem = async () => {
    if (loading) return;
    setLoading(true);

    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    const formattedQuantity = Math.round(newItem.quantity);

    try {
      const isDuplicateItem = items.some(
        (existingItem) => existingItem.itemID === newItem.itemID
      );

      if (isDuplicateItem) {
        setError(
          `Item "${
            filteredItems.find((item) => item.ItemID === newItem.itemID)
              ?.ItemName
          }" is already added.`
        );
        return;
      }

      const selectedItem = filteredItems.find(
        (item) => item.ItemID === newItem.itemID
      );

      const accountingUnitID = selectedItem?.AccountingUnitID;
      if (!accountingUnitID) {
        setError(
          "Could not determine the Accounting Unit for the selected item."
        );
        return;
      }

      await axios.post(`${API_BASE_URL}/phpage/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: formattedQuantity,
        Difference: formattedQuantity,
        Description: newItem.remarks || "",
        AccountingUnitID: accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
        CommitedDate: newItem.commitedDate,
      });

      const updatedItem = {
        ...newItem,
        itemName: selectedItem ? selectedItem.ItemName : "Unnamed Item",
        total: newItem.quantity * newItem.stock,
        accountingUnitID: accountingUnitID,
      };

      // Update local state immediately
      setItems((prevItems) => [...prevItems, updatedItem]);
      setModalOpen(false);
      setError(null);

      // Reset form
      setNewItem({
        categoryID: "",
        itemID: "",
        quantity: 1,
        difference: 1,
        stock: 0,
        total: 0,
        remarks: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        commitedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      });

      // Fetch updated data in background
      fetchItems2();

      onAddItem && onAddItem(updatedItem);
      onUpdateItem &&
        onUpdateItem({
          type: "add",
          item: updatedItem,
          changeLog: {
            ...changeLog,
            addedItems: [...changeLog.addedItems, updatedItem],
          },
        });
    } catch (error) {
      console.error("Error adding item:", error);
      setError("Failed to add item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (index) => {
    const itemToDelete = items[index];
    try {
      await axios.delete(
        `${API_BASE_URL}/phpage/indentitems/${itemToDelete.indentItemID}`,
        {
          // Use query parameters instead of request body
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      onDeleteItem && onDeleteItem(index);

      // Track deleted item
      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      // Notify parent component about the change
      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    }
  };

  const handleUpdateItem = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const itemToUpdate = editingItem;
      if (!itemToUpdate.itemID || !itemToUpdate.indentItemID) {
        setError("Invalid item ID");
        return;
      }

      const formattedQuantity = Math.round(editingItem.quantity);

      // First update the basic item details
      await axios.put(
        `${API_BASE_URL}/viewTable/indentitems/${editingItem.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: editingItem.stock,
          Description: editingItem.description,
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Update local description state
      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));

      // If user is admin and committed date is being updated, use the dedicated route
      if (isAdmin && editingItem.commitedDate) {
        await axios.put(
          `${API_BASE_URL}/viewTable/indentitems/committed-date/${editingItem.indentItemID}`,
          {
            CommitedDate: editingItem.commitedDate
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
      }

      // Refresh the items list to get the latest data
      const refreshResponse = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const refreshedItems = refreshResponse.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          // Format the CommitedDate to dd-mm-yy
          const formattedDate = item.CommitedDate
            ? new Date(item.CommitedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            normalDays: item.NormalDays,
            emergencyDays: item.EmergencyDays,
            commitedDate: formattedDate, // Use the formatted date
            accountingUnitID: item.AccountingUnitID,
            unitname: item.UnitName,
          };
        });

      setItems(refreshedItems);
      setEditingItem(null);
      onUpdateItem({ type: "update", item: editingItem });
    } catch (error) {
      console.error("Error updating item:", error);
      setError("Failed to update item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add real-time synchronization with polling
  useEffect(() => {
    if (!indentID) return;

    // Initial fetch
    refreshItemsData();

    // Set up polling for real-time updates (every 30 seconds)
    const pollInterval = setInterval(() => {
      refreshItemsData();
    }, 30000);

    // Cleanup interval on unmount or indentID change
    return () => {
      clearInterval(pollInterval);
    };
  }, [indentID]); // Only depend on indentID

  // Recalculate total amount based on local items state
  const calculateTotal = () => {
    return items.reduce((total, item) => total + item.total, 0);
  };

  // Add a method to reset change log when submitting
  const handleFinalSubmit = () => {
    onSubmit();
    // Reset change log after submission
    setChangeLog({
      addedItems: [],
      deletedItems: [],
      updatedItems: [],
    });
  };

  const handleItemSearch = useCallback(
    (selectedItem) => {
      // Handle null/clearing case
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "",
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          emergencyDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      // First set filtered items for the selected category
      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      // Get the normalDays and emergencyDays from the category
      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );
      const normalDays = selectedCategory?.NormalDays || 0;
      const emergencyDays = selectedCategory?.EmergencyDays || 0;

      // Calculate dates based on indent type
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      const daysToAdd = indentType === 'normal' ? normalDays : emergencyDays;
      promisedDate.setDate(currentDate.getDate() + daysToAdd);

      // Update all state in one go
      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.AU || "",
        stock: selectedItem.Stock || 0,
        normalDays: normalDays,
        emergencyDays: emergencyDays,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
      }));
    },
    [allItems, categories]
  );

  const isSubmittedStage =
    indentStatus.l1a === 1 &&
    indentStatus.l1 === 1 &&
    indentStatus.l2 === 1 &&
    indentStatus.po === 1 &&
    indentStatus.ph === 1;

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A to open add modal
      if (
        e.ctrlKey &&
        e.key.toLowerCase() === "a" &&
        !modalOpen &&
        isDraft &&
        isEditable &&
        !isSubmittedStage
      ) {
        e.preventDefault(); // Prevent default browser "Select All" behavior
        setModalOpen(true);
      }

      // Ctrl+S to save item
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault(); // Prevent the default browser action (save)

        // Trigger the "Add Item" function or button click
        document.getElementById("addItemButton").click(); // Assuming your button has this ID
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, editingItem, isDraft, isEditable, isSubmittedStage]);

  const renderActions = (item, index) => (
    <td className="px-4 py-3 text-center">
      <div className="flex justify-center space-x-2">
        {isDraft && isEditable && (
          <>
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => handleEditItem(item, index)}
            >
              <Edit size={18} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => handleDeleteClick(index, item.itemName)}
            >
              <Trash2 size={18} />
            </button>
          </>
        )}
      </div>
    </td>
  );

  const renderDaysInput = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <div className="col-span-1">
        <label className="block text-sm font-medium mb-1">
          {isEmergency ? "Days" : "Days"}
        </label>
        <input
          type="text"
          className="border rounded w-full px-3 py-2 bg-gray-100"
          value={
            isEmergency
              ? newItem.emergencyDays || "0"
              : newItem.normalDays || "0"
          }
          readOnly
        />
      </div>
    );
  };

  const handleDescriptionChange = useCallback((e) => {
    setEditingItem((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  }, []);

  const renderDescriptionCell = useCallback((itemId) => {
    if (editingItem?.itemID === itemId) {
      return (
        <div className="flex items-center space-x-2">
          <input
            key={`description-${itemId}-${editingItem.description}`}
            type="text"
            className="border rounded px-2 py-1 text-sm w-32"
            value={editingItem.description}
            onChange={handleDescriptionChange}
            autoFocus
          />
        </div>
      );
    }

    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  }, [editingItem, itemDescriptions, loadingDescriptions, handleDescriptionChange, fetchItemDescription]);

  const renderDateCell = (item) => {
    if (editingItem && editingItem.index === item.index) {
      // Convert the formatted date back to YYYY-MM-DD format for the input
      const convertToInputFormat = (dateStr) => {
        if (!dateStr || dateStr === "Not set") return "";
        try {
          const [day, month, year] = dateStr.split("/");
          return `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        } catch (error) {
          return "";
        }
      };

      return (
        <div className="flex items-center space-x-2">
          <input
            type="date"
            className="w-full border rounded px-2 py-1"
            value={convertToInputFormat(editingItem.commitedDate)}
            onChange={(e) =>
              setEditingItem((prev) => ({
                ...prev,
                commitedDate: e.target.value,
              }))
            }
          />
        </div>
      );
    }
    return <span className="text-right">{item.commitedDate || "Not set"}</span>;
  };

  const handleEditItem = (item, index) => {
    // Ensure we fetch the description if it hasn't been loaded yet
    if (!itemDescriptions[item.itemID]) {
      fetchItemDescription(item.itemID);
    }

    setEditingItem({
      ...item,
      index: index,
      indentItemID: item.indentItemID,
      quantity: item.quantity,
      stock: item.stock,
      description: itemDescriptions[item.itemID] || "",
      commitedDate: item.commitedDate, // Make sure to include the committed date
    });
    setItemsUpdated(true);
  };

  const PHDesktopRow = memo(function PHDesktopRow({
    item,
    index,
    isSubmittedStage,
    editingItem,
    renderDescriptionCell,
    renderDateCell,
    onQuantityChange,
    onSave,
    onCancel,
    onEdit,
    onDelete,
  }) {
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors duration-200">
        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
        <td className="px-4 py-3 font-medium">{item.itemName}</td>
        <td className="px-4 py-3 text-center">{renderDescriptionCell(item.itemID)}</td>
        <td className="px-4 py-3 font-medium">{item.unitname}</td>
        {editingItem && editingItem.index === index ? (
          <>
            <td className="px-4 py-3">
              <input
                type="number"
                className="w-full border rounded px-2 py-1"
                value={editingItem.quantity}
                onChange={(e) => onQuantityChange(e.target.value)}
              />
            </td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            <td className="px-4 py-3 text-right">
              {renderDateCell({ ...item, index })}
            </td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-green-500 hover:text-green-700" onClick={onSave}>Save</button>
                  <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>Cancel</button>
                </div>
              </td>
            )}
          </>
        ) : (
          <>
            <td className="px-4 py-3 text-center">{Math.round(item.quantity)}</td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            <td className="px-4 py-3 text-right">
              {renderDateCell({ ...item, index })}
            </td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-blue-500 hover:text-blue-700" onClick={() => onEdit(item, index)}>
                    <Edit size={18} />
                  </button>
                  <button className="text-red-500 hover:text-red-700" onClick={() => onDelete(index, item.itemName)}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            )}
          </>
        )}
      </tr>
    );
  });

  return (
    <div className="bg-white shadow-md rounded-lg px-4 border border-gray-200">
      {/* Reorganized header section */}
      <div className="flex justify-between items-center border-b">
        <div className="flex-1">
          {isDraft && isEditable && !isSubmittedStage && (
            <div className="text-sm text-gray-500">
              Keyboard shortcuts: Ctrl+A to add item, Ctrl+S to save
              {modalOpen ? ", Ctrl+S to save" : ""}
            </div>
          )}
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 flex-1 text-center">
          Indent Items
        </h2>

        <div className="flex items-center space-x-2 flex-1 justify-end">
          {/* Print Button */}
          <PrinterButton
            getPDFComponent={getPDFComponent}
            filename={`indent-${indentID}`}
            fetchData={fetchIndentData}
            onError={(error) => setPdfError("Failed to generate PDF. Please try again.")}
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
            title="Print Invoice"
          />

          {!isSubmittedStage && isDraft && isEditable && (
            <button
              className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
              onClick={() => setModalOpen(true)}
              title="Add Item"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Commited Date</th>
              {!isSubmittedStage && (
                <th className="px-4 py-3 text-center w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <PHDesktopRow
                  key={index}
                  item={item}
                  index={index}
                  isSubmittedStage={isSubmittedStage}
                  editingItem={editingItem}
                  renderDescriptionCell={renderDescriptionCell}
                  renderDateCell={renderDateCell}
                  onQuantityChange={(val) => setEditingItem((prev) => ({ ...prev, quantity: val }))}
                  onSave={handleUpdateItem}
                  onCancel={() => setEditingItem(null)}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteClick}
                />
              ))
            ) : (
              <tr>
                <td colSpan={!isSubmittedStage ? 7 : 6} className="text-center text-gray-500 py-6">
                  Loading items...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-between items-center">
        {/*<div className="text-lg font-bold text-gray-800">
          Total Invoice Amount:
          <span className="text-green-600 ml-2">
            ₹{calculateTotal().toLocaleString()}
          </span>
        </div>*/}
        <div className="flex space-x-4">
          {isSubmittedStage ? (
            <button
              className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
              disabled
            >
              Submitted
            </button>
          ) : (
            <>
              {isDraft && isEditable && (
                <>
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={() => {
                      if (onSaveDraft) {
                        onSaveDraft();
                      }
                    }}
                    disabled={items.length === 0 || !isDraft}
                  >
                    Save Draft
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={handleFinalSubmit}
                    disabled={items.length === 0 || !isDraft}
                  >
                    Submit Indent
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <Modal
          onClose={() => {
            setModalOpen(false);
            setError(null);
          }}
        >
          <div className="flex flex-col">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <h2 className="text-xl font-bold text-center mb-4">Add New Item</h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddNewItem();
              }}
            >
              <SearchRow
                allItems={allItems.map((item) => ({
                  ...item,
                  CategoryName: categories.find(
                    (cat) => cat.CategoryID === item.CategoryID
                  )?.CategoryName,
                }))}
                onSelectItem={handleItemSearch}
                selectedItemId={newItem.itemID}
              />

              <div className="grid grid-cols-12 gap-4 mb-4">
                <div className="col-span-3">
                  <label className="block text-sm font-medium mb-1">
                    Accounting Unit
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2 bg-gray-50"
                    value={newItem.accountingUnitName || "Not set"}
                    readOnly
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Up to 3 decimals"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Stock
                  </label>
                  <input
                    type="number"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.stock}
                    onChange={(e) =>
                      handleNewItemFieldChange(
                        "stock",
                        parseFloat(e.target.value)
                      )
                    }
                    required
                  />
                </div>
                {renderDaysInput()}
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">
                    Promised Date
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2 bg-gray-100"
                    value={
                      newItem.promisedDate
                        ? new Date(newItem.promisedDate).toLocaleDateString()
                        : "Not set"
                    }
                    readOnly
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium">Remarks</label>
                <textarea
                  key={newItem.itemID} // Add a stable key to force re-render
                  className="border rounded w-full px-3 py-2"
                  value={newItem.remarks}
                  onChange={(e) =>
                    handleNewItemFieldChange("remarks", e.target.value)
                  }
                  placeholder="Optional remarks about the item"
                  rows="3"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  id="addItemButton"
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded"
                >
                  Add Item
                </button>
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirmation.itemName}
      />
    </div>
  );
};

export default PHItemsTable;
